/**
 * Le vocabulaire du produit — un nom par notion, écrit une fois.
 *
 * ─── Ce que ce fichier corrige (TRA-ALL-COH-02, MOB-ELE-AUJ-09, MOB-ELE-NIV-07)
 *
 * La campagne a relevé, dans la MÊME liste de l'accueil de Léa, trois noms
 * pour un seul savoir : « Le périmètre du rectangle », « Périmètre d'un
 * rectangle », « Calcul du périmètre d'un rectangle ». Et entre surfaces, un
 * quatrième : l'accueil abrège en « L'aire d'un cercle » ce que la fiche
 * tuteur, la file et l'écran de progression nomment « Calculer la
 * circonférence et l'aire d'un cercle ».
 *
 * La cause n'est pas une faute de frappe. L'accueil groupait les tentatives
 * par **titre d'exercice** — une chaîne produite au tirage, différente à
 * chaque génération — et l'écran de progression par **savoir du référentiel**.
 * Deux surfaces comptaient donc deux choses différentes sous le même mot.
 *
 * ─── La règle ───────────────────────────────────────────────────────────────
 *
 * > Un savoir se nomme par l'énoncé du référentiel PDA, jamais par le titre
 * > d'un exercice qui le travaille, et jamais par son code.
 *
 * Le titre d'exercice reste ce qu'il est — le nom d'UNE tâche. Le code PDA
 * reste ce qu'il est — un identifiant technique, qui n'a rien à faire sous les
 * yeux d'un élève (`MOB-ELE-NIV-07` : `MAT-2-FRAC-ADD` s'affichait comme titre
 * d'écran, à la place du nom disponible juste en dessous).
 *
 * Module PUR, sans dépendance.
 */
/**
 * Le nom d'un savoir, tel qu'il doit apparaître à l'écran.
 *
 * @param enonce  L'énoncé du référentiel (`SavoirPDA.enonce`), quand il est
 *                connu. C'est la seule source légitime.
 * @param repli   Ce qu'on affiche à défaut — en pratique le titre de
 *                l'exercice, qui vaut mieux qu'un code ou qu'un vide.
 *
 * ⚠️ Le code PDA n'est JAMAIS un repli acceptable : `nommerSavoir` le refuse
 * explicitement, sans quoi la règle se contournerait d'un appel.
 */
export function nommerSavoir(enonce, repli) {
    const e = enonce?.trim();
    if (e && !estCodePda(e))
        return e;
    const r = repli.trim();
    return r && !estCodePda(r) ? r : 'Savoir du programme';
}
/** `MAT-2-FRAC-ADD`, `FR-4-COMP-CRIT` — la forme d'un code du référentiel. */
const CODE_PDA_RE = /^[A-Z]{2,4}-\d-[A-Z]+(-[A-Z]+)*$/;
/** `true` si la chaîne est un code du référentiel et non un nom lisible. */
export function estCodePda(valeur) {
    return CODE_PDA_RE.test(valeur.trim());
}
/**
 * Clé de regroupement d'une tentative par savoir.
 *
 * Le titre d'exercice n'est un repli qu'en l'ABSENCE de savoir rattaché : dès
 * qu'un savoir existe, c'est lui qui groupe — c'est exactement ce qui
 * empêchait l'accueil de recoller trois lignes en une.
 */
export function cleRegroupementSavoir(savoirId, titreExercice) {
    return savoirId ?? `titre:${titreExercice}`;
}
/* -------------------------------------------------------------------------- */
/* « Séance » et « jour de pratique » — deux faits, deux mots (lot 1.3)       */
/* -------------------------------------------------------------------------- */
/**
 * Nombres écrits en toutes lettres, de zéro à sept — la plage dont le produit
 * a besoin (les jours d'une semaine). Au-delà, on retombe sur le chiffre.
 * Forme FÉMININE (« une », pas « un ») : la seule employée ici.
 */
const EN_LETTRES = ['zéro', 'une', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept'];
/** « trois ». Le registre éditorial écrit les petits nombres en lettres. */
export function enLettres(n) {
    return EN_LETTRES[n] ?? String(n);
}
/**
 * « Cinq jours de pratique » / « Un jour de pratique » / « Aucun jour de
 * pratique » — le nom du fait que les quatre surfaces comptent.
 *
 * ─── Pourquoi ce n'est plus « Cinq séances » ───────────────────────────────
 *
 * L'audit BD (BD-03) a mesuré quatre chiffres pour « les séances de Léa » le
 * même jour : 5, 2, 4, 1. Tous exacts, aucun portant sur le même objet. Le
 * décompte du parent — le seul des quatre qui lisait les trois sources
 * d'activité — comptait en réalité des JOURS : « cinq séances » voulait dire
 * « cinq jours où elle a travaillé », et l'écran voisin de la même application
 * écrivait déjà « 5 jours de pratique » pour ce même nombre.
 *
 * Le mot « séance » est donc rendu à ce qu'il désigne : une `SessionTravail`,
 * une période continue de travail, dont un même jour peut porter deux
 * exemplaires (`packages/api/src/lib/seance.ts`). Ce que les surfaces
 * affichent est un dénombrement de jours, et il porte désormais son nom.
 *
 * Une phrase honnête vaut mieux qu'une phrase courte : un parent qui lit
 * « cinq séances » imagine cinq séances de travail, et le tuteur qui facture à
 * l'heure ne les trouve nulle part.
 */
export function nommerJoursDePratique(n) {
    if (n === 0)
        return 'Aucun jour de pratique';
    if (n === 1)
        return 'Un jour de pratique';
    return `${capitaliserPremiereLettre(enLettres(n))} jours de pratique`;
}
/** La même chose en milieu de phrase : « cinq jours de pratique ». */
export function nommerJoursDePratiqueMinuscule(n) {
    if (n === 0)
        return 'aucun jour de pratique';
    if (n === 1)
        return 'un jour de pratique';
    return `${enLettres(n)} jours de pratique`;
}
function capitaliserPremiereLettre(mot) {
    return mot.charAt(0).toUpperCase() + mot.slice(1);
}
/* -------------------------------------------------------------------------- */
/* « Observation » — un mot, trois grandeurs (lot 1.3, BD-02)                 */
/* -------------------------------------------------------------------------- */
/** Les deux dimensions, nommées pour un lecteur humain. */
const LIBELLE_DIMENSION = {
    calcul: 'en calcul',
    lecture_figure: 'en lecture de figure',
};
/**
 * « 9 observations (6 en calcul, 3 en lecture de figure) ».
 *
 * ─── Pourquoi la décomposition n'est pas facultative ───────────────────────
 *
 * `MOB-ELE-NIV-05` : un écran annonçait « 9 observations » sur un savoir, et
 * l'écran atteint en tapant cette même ligne en annonçait « 3 ». Les deux
 * étaient justes — 9 est le total des deux dimensions, 3 le journal de la
 * seule lecture de figure. Rien à l'écran ne permettait de le savoir.
 *
 * Un total sans sa décomposition n'est pas une information plus courte, c'est
 * une information qu'on ne peut pas rapprocher de la suivante. Sur un savoir à
 * une seule dimension, il n'y a rien à décomposer et la phrase reste courte.
 */
export function nommerObservations(total, parDimension = {}) {
    const nom = `${total} observation${total >= 2 ? 's' : ''}`;
    const parts = Object.entries(parDimension)
        .filter((entree) => typeof entree[1] === 'number')
        .filter(([, n]) => n > 0)
        .map(([dimension, n]) => `${n} ${LIBELLE_DIMENSION[dimension] ?? dimension}`);
    return parts.length >= 2 ? `${nom} (${parts.join(', ')})` : nom;
}
//# sourceMappingURL=vocabulaire.js.map